/**
 * Global State Store for the application
 */
import React, { createContext, useReducer } from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import Types from './Types';
import { BRANCH_LOCATOR_RADIUS } from '../../../constants';

const initialState = {
  geolocation: {
    branchProvince: '',
    branchAddress: '',
    branchCode: '',
  },
  requiredDocsModal: false,
  userGeolocationCoordinates: {},
  cityGeolocationCoordinates: {},
  cityGeolocationCoordinatesForEqualComparision: {},
  cityPageMapData: {},
  contactUsMapData: {},
  findBranchMapData: {},
  findABranchGeolocationCoordinates: {},
  findABranchGeolocationCoordinatesForEqualComparision: {},
  handleEmptyResponseData: true,
  calculatorData: {},
  autoLoanCalculatorData: {},
  radius: BRANCH_LOCATOR_RADIUS,
  isUserProvidedLocationAccess: Cookies.get('isUserProvidedLocationAccess') || false,
  provinceCoordinatesValue:
    (Cookies.get('provinceCoordinatesValue') &&
      JSON.parse(Cookies.get('provinceCoordinatesValue'))) ||
    {},
  provinceCoordinatesForEqualComparision: {},
  openLoanProductsModal: false,
  emailUsModal: false,
  emailusModalType: '',
  isSearchEnabled: false,
  mobileMenuStatus: false,
  searchModalValue: false,
  pageAnimation: {
    isEnabled: false,
    duration: 800,
    nextAnimationDelay: 500,
  },
  marketingModal: false,
  geoLocationProvince: false,
  mobileSearchAction: false,
  locationAllowed: false,
};
const store = createContext(initialState);
const { Provider } = store;

const StateProvider = ({ children }) => {
  const [state, dispatch] = useReducer((curState, action) => {
    switch (action.type) {
      case Types.CHANGE_PROVINCE:
        // This is an example handler for state change
        return {
          ...curState,
          geolocation: {
            branchAddress: action?.payload,
            branchProvince: action?.payload?.provinceStateC?.split('-')[1],
            branchCode: action?.payload?.provinceStateC?.split('-')[0],
          },
        };
      case Types.INIT_CALCULATOR:
        return {
          ...curState,
          calculatorData: action.payload,
        };
      case Types.INIT_AUTO_CALCULATOR:
        return {
          ...curState,
          autoLoanCalculatorData: action.payload,
        };
      case Types.STORE_PROVINCE:
        return {
          ...curState,
          geoLocationProvince: action.payload,
        };
      case Types.UPDATE_PAYMENT_FREQUENCY:
        return {
          ...curState,
          calculatorData: {
            ...curState.calculatorData,
            paymentFrequency: action.payload.paymentFrequency,
            amountToBorrow: action.payload.amountToBorrow,
            monthlyPaymentCalculation: action.payload.monthlyPaymentCalculation,
          },
        };
      case Types.UPDATE_TERM:
        return {
          ...curState,
          calculatorData: {
            ...curState.calculatorData,
            term: action.payload.term,
            amountToBorrow: action.payload.amountToBorrow,
            monthlyPaymentCalculation: action.payload.monthlyPaymentCalculation,
          },
        };
        case Types.UPDATE_YEAR:
          return {
            ...curState,
            autoLoanCalculatorData: {
              ...curState.autoLoanCalculatorData,
              year: action.payload.year,              
              term: action.payload.term,
              insurance: action.payload.insurance,
              monthlyPaymentCalculation: action.payload.monthlyPaymentCalculation,
            },
          }; 
        case Types.UPDATE_AUTO_LOAN_AMOUNT:
            return {
              ...curState,
              autoLoanCalculatorData: {
                ...curState.autoLoanCalculatorData,
                amountValue: action.payload.amountValue,
                amountToBorrow: action.payload.amountToBorrow,
                term: action.payload.term,
                insurance: action.payload.insurance,
                monthlyPaymentCalculation: action.payload.monthlyPaymentCalculation,
              },
            };           
        case Types.UPDATE_AMOUNT:
        return {
          ...curState,
          calculatorData: {
            ...curState.calculatorData,
            amountValue: action.payload.amountValue,
            amountToBorrow: action.payload.amountToBorrow,
            termRangeList: action.payload.termRangeList,
            insuranceList: action.payload.insuranceList,
            term: action.payload.term,
            step: action.payload.step,
            interest: action.payload.interest,
            monthlyPaymentCalculation: action.payload.monthlyPaymentCalculation,
          },
        };
      case Types.UPDATE_USER_GEOLOCATION_COORDINATES:
        return {
          ...curState,
          userGeolocationCoordinates: action.payload,
          radius: BRANCH_LOCATOR_RADIUS,
        };
      case Types.UPDATE_CITY_GEOLOCATION_COORDINATES:
        return {
          ...curState,
          cityGeolocationCoordinates: action.payload,
          radius: BRANCH_LOCATOR_RADIUS,
        };
      case Types.UPDATE_FIND_A_BRANCH_GEOLOCATION_COORDINATES:
        return {
          ...curState,
          findABranchGeolocationCoordinates: action.payload.data,
          handleEmptyResponseData: action.payload.handleEmptyResponseData,
          radius: BRANCH_LOCATOR_RADIUS,
        };
      case Types.UPDATE_BRANCH_LOCATION_COORDINATES:
        if (!action.payload.isProvinceChange) {
          Cookies.set(
            'provinceCoordinatesValue',
            JSON.stringify(action.payload.data),
            { expires: 90 },
          );
          Cookies.set(
            'isUserProvidedLocationAccess',
            action.payload.isLocationAccessGranted,
            { expires: 90 },
          );
        }
        return {
          ...curState,
          provinceCoordinatesValue: action.payload.data,
          isUserProvidedLocationAccess: action.payload.isLocationAccessGranted,
        };
      case Types.UPDATE_RADIUS:
        return {
          ...curState,
          radius: curState.radius + BRANCH_LOCATOR_RADIUS,
        };

      case Types.RESET_USER_COORDINATES:
        return {
          ...curState,
          userGeolocationCoordinates: {},
        };

      case Types.REQUIRE_DOCS_MODAL:
        return {
          ...curState,
          requiredDocsModal: action.payload,
        };
      case Types.UPDATE_CONTACT_US_MAP_DATA:
        return {
          ...curState,
          contactUsMapData: {
            ...curState.contactUsMapData,
            nearestLocationData: action.payload.data,
            userLocation: action.payload.userLocation,
          },
        };
      case Types.UPDATE_CITY_PAGE_MAP_DATA:
        return {
          ...curState,
          cityPageMapData: {
            ...curState.cityPageMapData,
            nearestLocationData: action.payload.data,
            userLocation: action.payload.userLocation,
          },
        };
      case Types.UPDATE_FIND_BRANCH_MAP_DATA:
        return {
          ...curState,
          findBranchMapData: {
            ...curState.findBranchMapData,
            nearestLocationData: action.payload.data,
            userLocation: action.payload.userLocation,
            loading: action.payload.loading,
          },
        };
      case Types.UPDATE_CITY_GEOLOCATION_COORDINATES_FOR_EQUAL_COMPARISION:
        return {
          ...curState,
          cityGeolocationCoordinatesForEqualComparision: action.payload,
        };
      case Types.UPDATE_PROVINCE_COORDINATES_FOR_EQUAL_COMPARISION:
        return {
          ...curState,
          provinceCoordinatesForEqualComparision: action.payload,
        };
      case Types.UPDATE_FIND_A_BRANCH_GEOLOCATION_COORDINATES_FOR_EQUAL_COMPARISION:
        return {
          ...curState,
          findABranchGeolocationCoordinatesForEqualComparision: action.payload,
        };

      case Types.OPEN_LOAN_PRODUCTS_MODAL:
        return {
          ...curState,
          openLoanProductsModal: action.payload,
        };
      case Types.TOGGLE_PAGE_ANIMATION:
        return {
          ...curState,
          pageAnimation: action.payload,
        };
      case Types.EMAIL_US_MODAL:
        return {
          ...curState,
          emailUsModal: action.payload.modal,
          emailusModalType: action.payload.type,
        };
        case Types.TRIGGER_MARKETING_MODAL:
          return {
            ...curState,
            marketingModal: action.payload.modal,
          };
  
      case Types.SEARCH_STATUS:
        return {
          ...curState,
          isSearchEnabled: action.payload,
        };
      case Types.MOBILE_MENU_STATUS:
        return {
          ...curState,
          mobileMenuStatus: action.payload,
        };
        case Types.MOBILE_SEARCH_BUTTON_ACTION:
          return {
            ...curState,
            mobileSearchAction: action.payload,
          };
      case Types.SEARCH_MODAL_STATUS:
        return {
          ...curState,
          searchModalValue: action.payload,
        };
      case Types.ALLOW_MAP_LOCATION:
        return {
          ...curState,
          locationAllowed: action.payload,
        };
      default:
        throw new Error();
    }
  }, initialState);

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

StateProvider.propTypes = {
  children: PropTypes.object,
};

export { store, StateProvider };
